import imageCoordinateCalculator from '@/utils/imageCoordinateCalculator';
import {
  defectGeomToOverlayQuadPx,
  resolveDefectShapeGeomForOverlay,
  scaleQuadToDisplay
} from '@/utils/defect/defectShapeOverlay';

/**
 * @param {object | null | undefined} imgPosCoords
 * @returns {number[][] | null}
 */
export function polygonRingFromImgPosCoords(imgPosCoords) {
  if (!imgPosCoords) {
    return null;
  }
  const raw = imgPosCoords.coordinates ?? imgPosCoords;
  if (!Array.isArray(raw) || raw.length === 0) {
    return null;
  }
  const ring = Array.isArray(raw[0]?.[0]) ? raw[0] : raw;
  if (!Array.isArray(ring) || ring.length < 3 || !Array.isArray(ring[0])) {
    return null;
  }
  const normalized = ring
    .map(pt => [Number(pt[0]), Number(pt[1])])
    .filter(([x, y]) => Number.isFinite(x) && Number.isFinite(y));
  return normalized.length >= 3 ? normalized : null;
}

/**
 * @param {number} x
 * @param {number} y
 * @param {number[][]} quad
 * @param {number} [marginPx]
 */
export function isPointInDisplayQuad(x, y, quad, marginPx = 0) {
  if (!quad || quad.length < 4) {
    return false;
  }
  const xs = quad.map(p => p[0]);
  const ys = quad.map(p => p[1]);
  const minX = Math.min(...xs) - marginPx;
  const maxX = Math.max(...xs) + marginPx;
  const minY = Math.min(...ys) - marginPx;
  const maxY = Math.max(...ys) + marginPx;
  return x >= minX && x <= maxX && y >= minY && y <= maxY;
}

/**
 * 지도 마커(경위도)와 IR 결함 사각형의 정렬 상태를 계산한다.
 * @param {object} params
 * @param {object} params.defect
 * @param {object} params.thermal
 * @param {number} params.lon
 * @param {number} params.lat
 * @param {number} params.displayW
 * @param {number} params.displayH
 * @param {number} [params.marginPx]
 */
export function computeDefectMapIrAlignment({
  defect,
  thermal,
  lon,
  lat,
  displayW,
  displayH,
  marginPx = 6
}) {
  const nativeW = Math.max(1, Number(thermal?.imgWidth) || 640);
  const nativeH = Math.max(1, Number(thermal?.imgHeight) || 512);
  const ring = polygonRingFromImgPosCoords(thermal?.imgPosCoords);
  if (!ring) {
    return { ok: false, reason: 'no_polygon' };
  }

  const pixel = imageCoordinateCalculator.geographicToPixel(
    { width: nativeW, height: nativeH },
    lon,
    lat,
    ring
  );
  if (!pixel) {
    return { ok: false, reason: 'projection_failed' };
  }

  const geom = resolveDefectShapeGeomForOverlay(defect);
  const quadPx = defectGeomToOverlayQuadPx(geom);
  if (!quadPx) {
    return { ok: false, reason: 'no_defect_shape' };
  }

  const defectQuadDisplay = scaleQuadToDisplay(quadPx, nativeW, nativeH, displayW, displayH);
  const mapPointDisplay = {
    x: (pixel.pixelX / nativeW) * displayW,
    y: (pixel.pixelY / nativeH) * displayH
  };

  const aligned = isPointInDisplayQuad(
    mapPointDisplay.x,
    mapPointDisplay.y,
    defectQuadDisplay,
    marginPx
  );

  const cx = defectQuadDisplay.reduce((sum, p) => sum + p[0], 0) / defectQuadDisplay.length;
  const cy = defectQuadDisplay.reduce((sum, p) => sum + p[1], 0) / defectQuadDisplay.length;
  const distancePx = Math.hypot(mapPointDisplay.x - cx, mapPointDisplay.y - cy);

  return {
    ok: true,
    aligned,
    defectQuadDisplay,
    mapPointDisplay,
    distancePx,
    nativeW,
    nativeH
  };
}

/**
 * SVG polygon points 속성 문자열
 * @param {number[][]} quad
 */
export function quadToSvgPoints(quad) {
  if (!quad?.length) {
    return '';
  }
  return quad.map(([x, y]) => `${x},${y}`).join(' ');
}
